/**
 * Play V2 / Circle client funnel analytics (RC1).
 * Interaction/funnel only — server PlayActivityEvent remains lifecycle authority.
 */

import { trackAgenticEvent } from '@/lib/analytics/agentic'
import { isNonProductionAnalyticsHost } from '@/lib/analytics/gaConfig'
import { getPreviewRevisionMarker } from '@/lib/play/previewRevisionMarker'

export const PLAY_V2_ANALYTICS_EVENTS = [
  'play_v2_viewed',
  'play_circle_viewed',
  'relationship_opened',
  'view_circle_opened',
  'connect_started',
  'connect_shared',
  'playdate_planning_started',
  'playdate_park_selected',
  'playdate_daypart_selected',
  'playdate_exact_time_selected',
  'playdate_review_viewed',
  'playdate_proposal_submitted',
  'playdate_proposal_succeeded',
  'playdate_proposal_failed',
  'playdate_invitation_opened',
  'playdate_accept_tapped',
  'playdate_decline_tapped',
  'playdate_complete_tapped',
  'playdate_cancel_tapped',
  'home_playdate_opened',
  'parks_playdate_handoff_started',
  'parks_playdate_handoff_consumed',
  'play_dog_profile_onboarding_viewed',
  'play_dog_profile_onboarding_selected',
] as const

export type PlayV2AnalyticsEvent = (typeof PLAY_V2_ANALYTICS_EVENTS)[number]

export type PlayV2AnalyticsSource =
  | 'play_tab'
  | 'home'
  | 'parks'
  | 'updates'
  | 'relationship'
  | 'orbit'
  | 'builder'
  | 'connect'
  | 'unknown'

/** Dimensions allowed on Play V2 client events. */
export type PlayV2AnalyticsParams = {
  activeDogId?: string | null
  relationshipId?: string | null
  playdateId?: string | null
  source?: PlayV2AnalyticsSource | string | null
  schedulingMode?: 'daypart' | 'exact' | null
  parkId?: string | null
  result?: string | null
  errorCategory?: string | null
  friendCount?: number | null
  /** Dog-profile onboarding card: no_dog | incomplete_dog | needs_dog_selection */
  onboardingState?: string | null
  /** create_dog | complete_dog | select_dog */
  destinationFlow?: string | null
}

const SENSITIVE_KEY_RE =
  /(token|email|note|password|secret|callback|guardian|phone|url)/i

const BLOCKED_PARAM_KEYS = new Set([
  'token',
  'connectToken',
  'publicToken',
  'email',
  'guardianName',
  'guardian_name',
  'note',
  'userNote',
  'notes',
  'callbackUrl',
  'callback_url',
  'dogCardUrl',
  'shareUrl',
])

export function isPreviewAnalyticsHost(
  hostname: string | null | undefined = typeof window !== 'undefined'
    ? window.location.hostname
    : null
): boolean {
  return isNonProductionAnalyticsHost(hostname)
}

export function buildPlayV2AnalyticsContext(input?: {
  hostname?: string | null
}): {
  is_preview: boolean
  environment: 'preview' | 'production'
  cloud_run_revision: string
  git_sha: string
} {
  const marker = getPreviewRevisionMarker()
  const preview = isPreviewAnalyticsHost(input?.hostname)
  return {
    is_preview: preview,
    environment: preview ? 'preview' : 'production',
    cloud_run_revision: marker.cloudRunRevision,
    git_sha: marker.shortSha,
  }
}

/** Strip sensitive / unknown keys; keep only safe funnel dimensions. */
export function sanitizePlayV2AnalyticsParams(
  params?: PlayV2AnalyticsParams | Record<string, unknown> | null
): Record<string, unknown> {
  if (!params) return {}
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue
    if (BLOCKED_PARAM_KEYS.has(key)) continue
    if (SENSITIVE_KEY_RE.test(key)) continue
    if (
      key === 'activeDogId' ||
      key === 'relationshipId' ||
      key === 'playdateId' ||
      key === 'source' ||
      key === 'schedulingMode' ||
      key === 'parkId' ||
      key === 'result' ||
      key === 'errorCategory' ||
      key === 'friendCount' ||
      key === 'onboardingState' ||
      key === 'destinationFlow'
    ) {
      out[key] = value
    }
  }
  return out
}

export function trackPlayV2Event(
  name: PlayV2AnalyticsEvent,
  params?: PlayV2AnalyticsParams,
  options?: { dedupeKey?: string }
): void {
  try {
    const safe = sanitizePlayV2AnalyticsParams(params)
    const ctx = buildPlayV2AnalyticsContext()
    trackAgenticEvent(
      name,
      {
        ...safe,
        ...ctx,
        surface: 'play_v2',
      },
      options?.dedupeKey ? { dedupeKey: options.dedupeKey } : undefined
    )
  } catch {
    // Never let analytics break Play UI (e.g. missing matchMedia in tests).
  }
}

/**
 * Legacy FeatureEvent keys PLAY_REQUEST_CREATED / PLAY_ACTIVATED:
 * still listed on admin activation dashboards but never emitted historically.
 * Do not map Circle client events to them (misleading match-era activation).
 * PLAY_VIEW remains the active Play activation signal.
 */
export const PLAY_LEGACY_FEATURE_KEYS_NOT_EMITTED = [
  'PLAY_REQUEST_CREATED',
  'PLAY_ACTIVATED',
] as const
